import { FC, useEffect, useMemo, useState } from "react";
import { LinearProgress, Paper, Stack, Typography } from "@mui/material";
import { useUserStore } from "../../../../../stores/user.store";
import useEventRatingsReport from "../../../../../hooks/useEventRatingsReport";
import ReportFiltersBar, {
	ReportFilters,
} from "../../components/ReportFiltersBar";
import EventRatingsSelectorBar, { ViewMode } from "./EventRatingsSelectorBar";
import EventRatingsKpiRow from "./EventRatingsKpiRow";
import ScoredSumView from "./ScoredSumView";
import IndependentRatingsView from "./IndependentRatingsView";

interface EventRatingsTabProps {
	programId: string;
	eventId: string;
	enrollmentId: string;
	onSelectionChange: (selection: {
		eventId?: string;
		enrollmentId?: string;
	}) => void;
}

interface LocalFilters extends ReportFilters {
	viewMode: ViewMode;
}

const EventRatingsTab: FC<EventRatingsTabProps> = ({
	programId,
	eventId,
	enrollmentId,
	onSelectionChange,
}) => {
	const { userInfo } = useUserStore();
	const accountId = userInfo?.accountId ?? "";

	const [filters, setFilters] = useState<LocalFilters>({
		orgUnitId: "",
		researcherId: "",
		startDate: null,
		endDate: null,
		viewMode: "population",
	});

	const { data, isLoading, isFetching } = useEventRatingsReport({
		accountId,
		programId,
		eventId: eventId || undefined,
		enrollmentId:
			filters.viewMode === "single" && enrollmentId ? enrollmentId : undefined,
		startDate: filters.startDate ? filters.startDate.toISOString() : undefined,
		endDate: filters.endDate ? filters.endDate.toISOString() : undefined,
	});

	const events = useMemo(() => data?.events ?? [], [data?.events]);
	const enrollments = useMemo(
		() => data?.enrollments ?? [],
		[data?.enrollments],
	);

	// Auto-pick first event the first time events arrive so the chart loads.
	useEffect(() => {
		if (!eventId && events.length > 0) {
			onSelectionChange({ eventId: events[0].eventId });
		}
	}, [events, eventId, onSelectionChange]);

	// Auto-pick first enrollment when switching to Single view.
	useEffect(() => {
		if (
			filters.viewMode === "single" &&
			!enrollmentId &&
			enrollments.length > 0
		) {
			onSelectionChange({ enrollmentId: enrollments[0].enrollmentId });
		}
	}, [filters.viewMode, enrollmentId, enrollments, onSelectionChange]);

	const noEvents = data && events.length === 0;

	return (
		<Stack spacing={3}>
			<ReportFiltersBar
				idPrefix="event-ratings"
				filters={filters}
				onChange={(next) => setFilters((p) => ({ ...p, ...next }))}
				hiddenFilters={["orgUnit", "researcher"]}
			/>

			<EventRatingsSelectorBar
				idPrefix="event-ratings"
				events={events}
				enrollments={enrollments}
				eventId={eventId}
				viewMode={filters.viewMode}
				enrollmentId={enrollmentId}
				onChange={(next) => {
					if (next.eventId !== undefined) {
						onSelectionChange({ eventId: next.eventId });
					}
					if (next.enrollmentId !== undefined) {
						onSelectionChange({ enrollmentId: next.enrollmentId });
					}
					if (next.viewMode !== undefined) {
						setFilters((p) => ({ ...p, viewMode: next.viewMode as ViewMode }));
					}
				}}
			/>

			{(isLoading || isFetching) && <LinearProgress />}

			{noEvents ? (
				<Paper variant="outlined" sx={{ p: 6, textAlign: "center" }}>
					<Typography variant="h6" gutterBottom>
						No rateable events for this program
					</Typography>
					<Typography variant="body2" color="text.secondary">
						This report shows trends for events whose form schema scores a sum
						(e.g. SOWS) or carries an emoji + distress pair (e.g. Mood / Sleep
						Check-in). Configure one in the program&apos;s treatment plan to
						start measuring ratings.
					</Typography>
				</Paper>
			) : data?.chart?.ratingType === "SCORED_SUM" && data.chart.scoredSum ? (
				<>
					<EventRatingsKpiRow
						mode="scoredSum"
						kpis={data.chart.scoredSum.kpis}
					/>
					<ScoredSumView payload={data.chart.scoredSum} />
				</>
			) : data?.chart?.ratingType === "INDEPENDENT_RATINGS" &&
			  data.chart.independent ? (
				<>
					<EventRatingsKpiRow
						mode="independent"
						kpis={data.chart.independent.kpis}
						emojiValues={data.chart.independent.emojiField.values}
					/>
					<IndependentRatingsView payload={data.chart.independent} />
				</>
			) : (
				<Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
					<Typography variant="body1" color="text.secondary">
						Select an event from the dropdown to load its ratings chart.
					</Typography>
				</Paper>
			)}
		</Stack>
	);
};

export default EventRatingsTab;
